import { FACEBOOK_APP_ID, defaultShareImage } from 'constants/social';
import { FunctionComponent, useEffect } from 'react';
import { Helmet, HelmetProps } from 'react-helmet-async';
import { useRouteMatch } from 'react-router';

export interface HeadProps {
  /**
   * if true, sets up default helmet settings, should only be set once ideally, or if you want to re-default it in
   * a particular component tree in the app
   */
  initializeDefaultSettings?: boolean;
  description?: string;
  /** image is required for Facebook and Twitter
   * @see https://developers.facebook.com/docs/sharing/best-practices#images
   * @see https://dev.twitter.com/cards/types/summary-large-image
   * defaults to tock share image const defaultShareImage
   */
  image?: string;
  keywords?: string;
  /**
   * maps to twitter:site attribute
   * @see https://dev.twitter.com/cards/types/summary
   * defaults to `@tock`
   */
  site?: string;
  /**
   * maps to og:site_name, defaults to Tock
   * @see https://developers.facebook.com/docs/sharing/best-practices#sharing-on-facebook
   */
  siteName?: string;
  /**
   * maps to title attribute, can be undefined in case of nesting components
   */
  title?: string;
  /**
   * maps to og:type
   * @see https://developers.facebook.com/docs/sharing/best-practices#types
   * defaults to 'website'
   */
  type?: string;
}

export const Head: FunctionComponent<HeadProps> = ({
  children,
  initializeDefaultSettings = false,
  description,
  image = defaultShareImage,
  keywords,
  site = '@tock',
  siteName = 'Tock',
  title,
  type = 'website',
}) => {
  const match = useRouteMatch();
  const helmetProps: HelmetProps = {};
  if (initializeDefaultSettings) {
    helmetProps.defaultTitle = 'Tock';
    helmetProps.titleTemplate = '%s | Tock';
  }

  // selectively apply maximum-scale=1 to mobile safari only in order to prevent
  // unwanted input zooming, while avoiding a11y zoom issues for android, etc.
  // https://weblog.west-wind.com/posts/2023/Apr/17/Preventing-iOS-Safari-Textbox-Zooming#selective-maximum-scale-for-safari-on-ios
  useEffect(() => {
    if (navigator.userAgent.indexOf('iPhone') > -1) {
      document
        .querySelector('[name=viewport')
        ?.setAttribute(
          'content',
          'width=device-width, initial-scale=1, maximum-scale=1, viewport-fit=cover'
        );
    }
  }, []);

  return (
    <Helmet {...helmetProps}>
      {title ? <title>{title}</title> : null}
      {description ? <meta name="description" content={description} /> : null}
      {keywords ? <meta name="keywords" content={keywords} /> : null}
      <meta property="fb:app_id" content={FACEBOOK_APP_ID} />
      {title ? <meta property="og:title" content={title} /> : null}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:image" content={image} />
      <meta property="og:url" content={`https://www.exploretock.com${match.url}`} />
      {description ? <meta property="og:description" content={description} /> : null}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:site" content={site} />
      {title ? <meta property="twitter:title" content={title} /> : null}
      {description ? <meta property="twitter:description" content={description} /> : description}
      <meta property="twitter:image" content={image} />
      <link rel="canonical" href={`https://www.exploretock.com${match.url}`} />
      {children}
    </Helmet>
  );
};
